import { typeLabel, type Critter } from "../creatures/dna";
import {
  bodyIndexForCreature,
  primaryProgramId,
  resolveKnownProgram,
} from "../data/programs";
import type { TxDetail } from "../data/types";
import { BODY_CREATURE_KEYS } from "../sprites/assets";
import {
  dragonHeightLabel,
  formatBlockHeights,
  type SeaDragon,
} from "../sprites/dragons";
import { explorerBlockUrl, explorerTxUrl } from "../sprites/pixelFont";
import { toBase58Id } from "../util/base58";

export type EntityDetail =
  | {
      kind: "tx";
      txid: string;
      displayId: string;
      name: string;
      species: string;
      type: string;
      /** Theater phase (shore / boarding / fled…). */
      status: string;
      /** Chain execution: success / failed / pending. */
      result: string;
      ageLabel: string;
      blockLabel: string | null;
      explorerUrl: string;
      shiny: boolean;
      legendary: boolean;
      /** Primary program pubkey (base58 when convertible). */
      programId: string | null;
      programLabel: string | null;
      programCount: number;
      feeLabel: string | null;
      signerLabel: string | null;
      accountsLabel: string | null;
      instructionsLabel: string | null;
      confirmedLabel: string | null;
    }
  | {
      kind: "block";
      height: number;
      label: string;
      hash: string;
      explorerUrl: string;
      riderName: string | null;
    };

const SPECIES_TITLE: Record<string, string> = {
  tideling: "Tideling",
  mosscurl: "Mosscurl",
  sparkwick: "Sparkwick",
  gloamink: "Gloamink",
  reefbit: "Reefbit",
};

export function speciesFromBody(body: number): string {
  const key = BODY_CREATURE_KEYS[body % BODY_CREATURE_KEYS.length]!;
  return SPECIES_TITLE[key] ?? key;
}

function phaseStatus(c: Critter): string {
  switch (c.phase) {
    case "wild":
      return "Waiting on shore";
    case "catching":
      return "Boarding sea dragon";
    case "caught":
      return "Confirmed in block";
    case "fled":
      return "Failed / fled";
    default:
      return c.phase;
  }
}

function resultLabel(c: Critter): string {
  switch (c.result) {
    case "success":
      return "success";
    case "failed":
      return "failed";
    default:
      return "pending";
  }
}

export function formatAge(addedAt: number, now = Date.now()): string {
  const ms = Math.max(0, now - addedAt);
  if (ms < 1_000) return "just now";
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3_600_000)}h ago`;
}

function formatFeeArch(fee: number | null | undefined): string | null {
  if (fee == null || !Number.isFinite(fee)) return null;
  if (fee === 0) return "0 ARCH";
  if (fee < 0.000001) return `${fee.toExponential(2)} ARCH`;
  return `${fee} ARCH`;
}

export function detailFromCritter(
  c: Critter,
  network: "mainnet" | "testnet",
): EntityDetail {
  const displayId = toBase58Id(c.ref.txid);
  const known = resolveKnownProgram(c.programIds);
  return {
    kind: "tx",
    txid: c.ref.txid,
    displayId,
    name: c.name,
    species: speciesFromBody(c.body),
    type: typeLabel(c.type),
    status: phaseStatus(c),
    result: resultLabel(c),
    ageLabel: formatAge(c.addedAt),
    blockLabel:
      c.blockHeight != null && c.blockHeight > 0
        ? formatBlockHeights([c.blockHeight]) || null
        : null,
    explorerUrl: explorerTxUrl(c.ref.txid, network),
    shiny: c.shiny,
    legendary: c.legendary,
    programId: primaryProgramId(c.programIds),
    programLabel: c.programLabel ?? known?.label ?? null,
    programCount: c.programIds.length,
    feeLabel: c.feePriority > 0 ? `priority ${c.feePriority}` : null,
    signerLabel: null,
    accountsLabel: null,
    instructionsLabel: null,
    confirmedLabel: null,
  };
}

function resultFromRest(rest: TxDetail, fallback: string): string {
  if (!rest.status) {
    if (rest.success === false) return "failed";
    return fallback;
  }
  if (!rest.success || /^fail/i.test(rest.status)) {
    return rest.status.startsWith("Failed") ? rest.status : "failed";
  }
  if (rest.status === "Processed") return "success";
  if (rest.status === "Queued") return "pending";
  return rest.status;
}

/** Merge explorer REST fields into an existing tx detail (drawer enrich). */
export function mergeTxDetail(
  base: Extract<EntityDetail, { kind: "tx" }>,
  rest: TxDetail,
): Extract<EntityDetail, { kind: "tx" }> {
  const programIds =
    rest.program_ids.length > 0
      ? rest.program_ids
      : base.programId
        ? [base.programId]
        : [];
  const known = resolveKnownProgram(programIds);
  const programId = primaryProgramId(programIds) ?? base.programId;
  const mappedBody = known ? bodyIndexForCreature(known.body) : null;
  const species =
    mappedBody != null ? speciesFromBody(mappedBody) : base.species;

  return {
    ...base,
    species,
    result: resultFromRest(rest, base.result),
    blockLabel:
      rest.block_height != null && rest.block_height > 0
        ? formatBlockHeights([rest.block_height]) || base.blockLabel
        : base.blockLabel,
    programId,
    programLabel: rest.program_label ?? known?.label ?? base.programLabel,
    programCount: Math.max(programIds.length, base.programCount),
    feeLabel: formatFeeArch(rest.fee_estimated_arch) ?? base.feeLabel,
    signerLabel:
      rest.from_address != null
        ? toBase58Id(rest.from_address)
        : rest.signers != null
          ? `${rest.signers} signer${rest.signers === 1 ? "" : "s"}`
          : base.signerLabel,
    accountsLabel:
      rest.accounts_count != null
        ? String(rest.accounts_count)
        : base.accountsLabel,
    instructionsLabel:
      rest.instructions_count != null
        ? String(rest.instructions_count)
        : base.instructionsLabel,
    confirmedLabel:
      rest.confirmed_at ?? rest.created_at ?? base.confirmedLabel,
  };
}

export function detailFromDragon(
  d: SeaDragon,
  network: "mainnet" | "testnet",
  riderName: string | null,
): EntityDetail {
  return {
    kind: "block",
    height: d.height,
    label: dragonHeightLabel(d),
    hash: d.hash.startsWith("height:") ? "—" : d.hash,
    explorerUrl:
      d.height > 0 && d.height < 1_000_000_000
        ? explorerBlockUrl(d.height, network)
        : "https://explorer.arch.network/testnet",
    riderName,
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, "&#39;");
}

function row(label: string, valueHtml: string): string {
  return `<div><dt>${escapeHtml(label)}</dt><dd>${valueHtml}</dd></div>`;
}

function monoCopy(value: string, copyKind: string): string {
  return `<span class="mono-row"><span class="mono">${escapeHtml(value)}</span><button type="button" class="copy-btn" data-copy="${escapeAttr(value)}" data-copy-kind="${escapeAttr(copyKind)}" aria-label="Copy ${escapeAttr(copyKind)}">copy</button></span>`;
}

/** HTML for the creature (tx) drawer body. */
export function renderTxDetailHtml(
  detail: Extract<EntityDetail, { kind: "tx" }>,
  opts?: { enriching?: boolean },
): string {
  const traits = [
    detail.shiny ? "shiny" : null,
    detail.legendary ? "legendary" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const programValue = detail.programId
    ? `${monoCopy(detail.programId, "program id")}${
        detail.programLabel
          ? `<div class="sub">${escapeHtml(detail.programLabel)}${
              detail.programCount > 1
                ? ` · +${detail.programCount - 1} more`
                : ""
            }</div>`
          : detail.programCount > 1
            ? `<div class="sub">+${detail.programCount - 1} more</div>`
            : ""
      }`
    : opts?.enriching
      ? `<span class="muted">loading…</span>`
      : `<span class="muted">unknown</span>`;

  const parts: string[] = [
    row("transaction", monoCopy(detail.displayId, "transaction")),
    row("program", programValue),
    row("age", escapeHtml(detail.ageLabel)),
    row("result", escapeHtml(detail.result)),
    row("status", escapeHtml(detail.status)),
    row(
      "species",
      `${escapeHtml(detail.species)} · ${escapeHtml(detail.type)}${
        traits ? ` · ${escapeHtml(traits)}` : ""
      }`,
    ),
    row(
      "block",
      escapeHtml(detail.blockLabel ?? "pending confirmation"),
    ),
  ];

  if (detail.feeLabel) parts.push(row("fee", escapeHtml(detail.feeLabel)));
  if (detail.signerLabel) {
    parts.push(
      row(
        "signer",
        detail.signerLabel.length > 20
          ? monoCopy(detail.signerLabel, "signer")
          : escapeHtml(detail.signerLabel),
      ),
    );
  }
  if (detail.accountsLabel) {
    parts.push(row("accounts", escapeHtml(detail.accountsLabel)));
  }
  if (detail.instructionsLabel) {
    parts.push(row("instructions", escapeHtml(detail.instructionsLabel)));
  }
  if (detail.confirmedLabel) {
    parts.push(row("confirmed", escapeHtml(detail.confirmedLabel)));
  }

  return `
      <dl>
        ${parts.join("\n        ")}
      </dl>
      <div class="drawer-actions">
        <a href="${escapeAttr(detail.explorerUrl)}" target="_blank" rel="noopener noreferrer"
          aria-label="View in Arch Explorer (opens in new tab)">View in Arch Explorer</a>
      </div>
    `;
}

export function renderBlockDetailHtml(
  detail: Extract<EntityDetail, { kind: "block" }>,
): string {
  return `
      <dl>
        <div>
          <dt>height</dt>
          <dd>${escapeHtml(detail.label)}</dd>
        </div>
        <div>
          <dt>hash</dt>
          <dd class="mono">${escapeHtml(detail.hash)}</dd>
        </div>
        <div>
          <dt>rider</dt>
          <dd>${escapeHtml(detail.riderName ?? "none yet")}</dd>
        </div>
      </dl>
      <div class="drawer-actions">
        <a href="${escapeAttr(detail.explorerUrl)}" target="_blank" rel="noopener noreferrer"
          aria-label="View block in Arch Explorer (opens in new tab)">View in Arch Explorer</a>
      </div>
    `;
}
